import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { ApiError } from "@/api/client";
import {
  PdfVorlageInfo,
  deletePdfVorlage,
  downloadPdfVorlage,
  listPdfVorlagen,
  pdfVorlageBuiltinUrl,
  pdfVorlageCustomUrl,
  setPdfVorlagenAuswahl,
  uploadPdfVorlage,
} from "@/api/pdfVorlagen";

type Props = { isAdmin: boolean };

function VorlageCard({ info, isAdmin }: { info: PdfVorlageInfo; isAdmin: boolean }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function showErr(e: unknown, fallback: string) {
    setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : fallback);
    setMsg(null);
  }

  const auswahlMut = useMutation({
    mutationFn: (name: string | null) => setPdfVorlagenAuswahl(info.doc_type, name),
    onSuccess: async () => {
      setMsg("Auswahl gespeichert.");
      setErr(null);
      await qc.invalidateQueries({ queryKey: ["pdf-vorlagen"] });
    },
    onError: (e: unknown) => showErr(e, "Auswahl konnte nicht gespeichert werden."),
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadPdfVorlage(info.doc_type, file),
    onSuccess: async (res) => {
      setMsg(`Hochgeladen: ${res.dateiname}`);
      setErr(null);
      if (fileRef.current) fileRef.current.value = "";
      await qc.invalidateQueries({ queryKey: ["pdf-vorlagen"] });
    },
    onError: (e: unknown) => showErr(e, "Upload fehlgeschlagen."),
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => deletePdfVorlage(info.doc_type, name),
    onSuccess: async () => {
      setMsg("Vorlage gelöscht.");
      setErr(null);
      await qc.invalidateQueries({ queryKey: ["pdf-vorlagen"] });
    },
    onError: (e: unknown) => showErr(e, "Löschen fehlgeschlagen."),
  });

  function onSelectChange(value: string) {
    auswahlMut.mutate(value === "__builtin__" ? null : value);
  }

  function onFilePick() {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".html")) {
      setErr("Bitte eine .html-Datei wählen.");
      return;
    }
    uploadMut.mutate(f);
  }

  async function onDownloadBuiltin() {
    if (!info.builtin_dateiname) return;
    try {
      await downloadPdfVorlage(pdfVorlageBuiltinUrl(info.doc_type), info.builtin_dateiname);
    } catch (e) {
      showErr(e, "Download fehlgeschlagen.");
    }
  }

  async function onDownloadCustom(name: string) {
    try {
      await downloadPdfVorlage(pdfVorlageCustomUrl(info.doc_type, name), name);
    } catch (e) {
      showErr(e, "Download fehlgeschlagen.");
    }
  }

  const activeValue = info.auswahl ?? "__builtin__";

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionHeader
        title={info.label}
        desc="HTML-Vorlage für die PDF-Erzeugung. Die mitgelieferte Vorlage dient als Fallback."
      />

      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, color: "var(--fg-muted)" }}>Aktive Vorlage:</label>
          <select
            value={activeValue}
            disabled={!isAdmin || auswahlMut.isPending}
            onChange={(e) => onSelectChange(e.target.value)}
            style={{
              padding: "6px 10px",
              border: "1px solid var(--border-strong)",
              borderRadius: 8,
              fontFamily: "var(--font-body)",
              fontSize: 13,
              background: "var(--bg-card)",
              color: "var(--fg-default)",
            }}
          >
            <option value="__builtin__">
              Standard (mitgeliefert{info.builtin_dateiname ? `: ${info.builtin_dateiname}` : ""})
            </option>
            {info.custom_vorlagen.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 6 }}>
            Mitgelieferte Vorlage
          </div>
          {info.builtin_vorhanden && info.builtin_dateiname ? (
            <Btn icon="download" onClick={onDownloadBuiltin}>
              {info.builtin_dateiname} herunterladen
            </Btn>
          ) : (
            <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
              (keine mitgelieferte Vorlage gefunden)
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 6 }}>
            Eigene Vorlagen
          </div>
          {info.custom_vorlagen.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
              Noch keine eigenen Vorlagen hochgeladen.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {info.custom_vorlagen.map((name) => {
                const aktiv = info.auswahl === name;
                return (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      border: "1px solid var(--border-strong)",
                      borderRadius: 8,
                      background: aktiv ? "var(--bg-selected)" : "var(--bg-card)",
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 13 }}>
                      {name}
                      {aktiv && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: "var(--brand-900)",
                            fontWeight: 600,
                          }}
                        >
                          AKTIV
                        </span>
                      )}
                    </span>
                    <Btn icon="download" onClick={() => onDownloadCustom(name)}>
                      Download
                    </Btn>
                    {isAdmin && (
                      <Btn
                        icon="delete"
                        variant="danger"
                        disabled={aktiv || deleteMut.isPending}
                        onClick={() => {
                          if (window.confirm(`Vorlage „${name}“ wirklich löschen?`)) {
                            deleteMut.mutate(name);
                          }
                        }}
                      >
                        Löschen
                      </Btn>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {isAdmin && (
          <div>
            <div style={{ fontSize: 12, color: "var(--fg-muted)", marginBottom: 6 }}>
              Neue Vorlage hochladen (.html, max. 1 MB)
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                ref={fileRef}
                type="file"
                accept=".html,text/html"
                style={{ fontSize: 13 }}
              />
              <Btn
                icon="upload"
                variant="primary"
                onClick={onFilePick}
                disabled={uploadMut.isPending}
              >
                Hochladen
              </Btn>
            </div>
          </div>
        )}

        {(msg || err) && (
          <div
            style={{
              fontSize: 12,
              padding: "6px 10px",
              borderRadius: 6,
              background: err ? "var(--bg-danger-subtle, #fde8e8)" : "var(--bg-subtle)",
              color: err ? "var(--fg-danger, #b42318)" : "var(--fg-muted)",
            }}
          >
            {err ?? msg}
          </div>
        )}
      </div>
    </Card>
  );
}

export function PdfVorlagenSection({ isAdmin }: Props) {
  const q = useQuery({ queryKey: ["pdf-vorlagen"], queryFn: listPdfVorlagen });

  if (q.isLoading) {
    return (
      <Card>
        <div style={{ fontSize: 13, color: "var(--fg-muted)" }}>Lade …</div>
      </Card>
    );
  }
  if (q.error) {
    const msg = q.error instanceof ApiError ? q.error.message : "Konnte Vorlagen nicht laden.";
    return (
      <Card>
        <div style={{ fontSize: 13, color: "var(--fg-danger, #b42318)" }}>{msg}</div>
      </Card>
    );
  }

  return (
    <>
      {!isAdmin && (
        <Card style={{ marginBottom: 16, background: "var(--bg-subtle)" }}>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            <span className="material-icons-outlined" style={{ fontSize: 16, verticalAlign: "-3px" }}>
              info
            </span>{" "}
            Nur lesbar — Hochladen, Löschen und Auswählen können nur Administratoren.
          </div>
        </Card>
      )}
      {(q.data ?? []).map((info) => (
        <VorlageCard key={info.doc_type} info={info} isAdmin={isAdmin} />
      ))}
    </>
  );
}
